import { CSSProperties, forwardRef, HTMLAttributes, ReactElement, ReactNode, Ref } from 'react';

import { IComponent } from '../../models/components.models';
import { Sizes } from '../../themes/styles';
import { useClassNames } from '../../utils/hooks';
import { singleOrArrayToArray } from '../../utils/utils';
import { cardListStylesDefault, ICardListStyles } from './CardList.styles';

export interface IItemMetaData {
  themes?: string[];
  colSpan?: number;
  rowSpan?: number;
}

interface ICardListClassNames {
  root: string;
  item: string;
}

export interface ICardListClicItemEvent<T> {
  data: T;
}

export interface ICardListProps<T extends { [key: string]: any }>
  extends IComponent<ICardListStyles>,
    HTMLAttributes<HTMLDivElement> {
  idField?: keyof T;
  nameField?: keyof T;
  gap?: number;
  data?: T[];
  metaDatas?: IItemMetaData[];
  columnCount: number;
  itemRenderer?: (item: T, meta: IItemMetaData, index: number) => ReactNode | undefined;
  onClickItem?: (e: ICardListClicItemEvent<T>) => void;
}

export const CardList = forwardRef(
  <T extends { [key: string]: any }>(props: ICardListProps<T>, ref?: Ref<HTMLDivElement>) => {
    const {
      className,
      styles,
      idField = 'id' as keyof T,
      nameField = 'name' as keyof T,
      data = [],
      metaDatas = [],
      gap = Sizes.cardGap,
      columnCount = 0,
      itemRenderer,
      onClickItem,
      ...elementProps
    } = props;
    const componentStyles = {
      root: {
        gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
        marginTop: `-${gap}px`, // fluent doesn't support 'gap' because IE11 doesn't support it
        marginLeft: `-${gap}px`,
      },
      item: {
        marginTop: gap,
        marginLeft: gap,
      },
    };
    const classNames = useClassNames<ICardListStyles, ICardListClassNames>(
      'CardList',
      cardListStylesDefault,
      [componentStyles, ...singleOrArrayToArray(styles)],
      className
    );
    const renderItems = () => {
      return data.map((d, i) => {
        const { themes = [], colSpan = 1, rowSpan = 1 } = metaDatas[i] || {};
        const itemStyle: CSSProperties = {};
        if (colSpan > 1 || rowSpan > 1) {
          itemStyle.gridColumn = `span ${colSpan}`;
          itemStyle.gridRow = `span ${rowSpan}`;
          if (!themes.includes(`h2o-CardList-item--${colSpan}by${rowSpan}`)) {
            themes.push(`h2o-CardList-item--${colSpan}by${rowSpan}`);
          }
        }
        metaDatas[i] = metaDatas[i] ? { ...metaDatas[i], themes } : { themes };
        return (
          <div
            className={[classNames.item].concat(themes).join(' ')}
            style={itemStyle}
            key={`${d[idField]}`}
            onClick={onClickItem ? () => onClickItem({ data: d }) : undefined}
          >
            {itemRenderer ? itemRenderer(d, metaDatas[i], i) : `${d[nameField]}`}
          </div>
        );
      });
    };
    return columnCount > 0 ? (
      <div {...elementProps} ref={ref} className={classNames.root}>
        {renderItems()}
      </div>
    ) : null;
  }
) as <T extends { [key: string]: any }>(props: ICardListProps<T> & { ref?: Ref<HTMLDivElement> }) => ReactElement;
